<template>
  <listing-layout>
    <div slot="heading" class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="h2 mb-0">Switch Committee</h1>
    </div>

    <div slot="content">
      <div class="alert alert-info">
        When you switch to a committee you're not part of, you'll have limited access to its functionality.
        You'll be able to manage the selected committee's nominations, drafts, draws, and reports. However,
        the Committee page will be view-only, and you won't have access to other Secretaries' personal information.
      </div>
      <div class="alert alert-warning" v-if="errorMessage">{{ errorMessage }}</div>

      <div class="form-inline mb-sm-3 d-flex justify-content-end">
        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
          <label for="committeesSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0">
            <input
              id="committeesSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No committees found."
          @sort="setSort"
        >
          <template #cell:name="{ row }">
            <list-name-cell align="right" :label="row.name">
              <template #badges>
                <span v-if="isOwn(row)" class="badge alert-info">My Committee</span>
              </template>
            </list-name-cell>
          </template>

          <template #cell:switch="{ row }">
            <button class="btn btn-sm btn-primary text-nowrap committee-action-btn" :disabled="isCurrent(row)" @click="switchTo(row)">
              {{ isCurrent(row) ? 'Current' : 'Switch' }}
            </button>
          </template>
        </responsive-table>
      </div>

      <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="committeesShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="committeesShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>
    </div>
  </listing-layout>
</template>

<script>
import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import ListNameCell from '@/components/ListNameCell'
import useSwitchableCommittees from '@/composables/committees/useSwitchableCommittees'
import { getActingCommittee, setActingCommittee } from '@/services/actingCommittee'
import { buildColumns, buildBreakpoints } from './switchCommitteeColumns'

// CAM-1533: Super Secretary committee switcher. The API lists financial committees only and
// rejects (acting_committee_invalid) any other in X-Acting-Committee.
export default defineComponent({
  name: 'SwitchCommittee',
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    ListNameCell
  },
  setup() {
    const {
      rows, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch
    } = useSwitchableCommittees()

    const columns = buildColumns()
    const breakpoints = buildBreakpoints()
    const errorMessage = ref('')

    const ownCommitteeId = localStorage.own_committee_id ? Number(localStorage.own_committee_id) : null
    const isOwn = committee => committee.id === ownCommitteeId
    function isCurrent(committee) {
      const acting = getActingCommittee()
      return committee.id === (acting ? acting.id : ownCommitteeId)
    }

    // The user's own committee returns to normal: no header is sent any more.
    function switchTo(committee) {
      setActingCommittee(isOwn(committee) ? null : committee)
      // Every page's data is committee-scoped — start clean on the nominations dashboard
      window.location.assign('/committees/dashboard')
    }

    const searchInput = ref(filters.search || '')
    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    onMounted(() => fetch())

    return {
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      errorMessage,
      setPage,
      setSort,
      setPerPage,
      applySearch,
      isOwn,
      isCurrent,
      switchTo
    }
  }
})
</script>

<style scoped>
/* Bootstrap's .btn-sm is still sized for a longer label — shrink the row button to match the
   Horses list's Remove button (same values as .member-action-btn / .secretary-login-btn). */
.committee-action-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .committee-action-btn {
    padding: 0.15rem 0.35rem;
    font-size: 0.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Committees/Switch/SwitchCommittee.vue